<template>
    <div class="page">
        <IndexHeader />

        <main class="page-main">
            <!-- 无 tenant_key 错误提示 -->
            <div v-if="!tenantKey" class="error-card">
                <div class="error-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12 9V13M12 17H12.01M5.07 19H18.93C20.47 19 21.44 17.33 20.66 16L13.73 4C12.96 2.67 11.04 2.67 10.27 4L3.34 16C2.56 17.33 3.53 19 5.07 19Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <h2>访问错误</h2>
                <p>缺少租户标识（tenant_key），无法加载小店内容。</p>
                <p class="error-hint">请通过正确的租户小店链接访问。</p>
            </div>

            <!-- 小店内容 -->
            <div v-else class="shop-container">
                <!-- 小店头部 -->
                <div class="shop-header">
                    <div class="shop-header-info">
                        <h1 class="shop-title">{{ shopInfo.shop_name || '小店' }}</h1>
                        <p class="shop-subtitle" v-if="shopInfo.shop_desc">{{ shopInfo.shop_desc }}</p>
                        <p class="shop-contact" v-if="shopInfo.contact">联系方式：{{ shopInfo.contact }}</p>
                    </div>
                    <a-button class="orders-btn" @click="goOrders">
                        <template #icon>
                            <TicketsOutlined />
                        </template>
                        订单查询
                    </a-button>
                </div>

                <!-- 分类标签 -->
                <div class="category-tabs" v-if="categories.length > 0">
                    <div class="category-tab" :class="{ active: activeCategory === null }"
                        @click="selectCategory(null)">全部</div>
                    <div class="category-tab" :class="{ active: activeCategory === cat.id }" v-for="cat in categories"
                        :key="cat.id" @click="selectCategory(cat.id)">
                        {{ cat.name }}
                    </div>
                </div>

                <!-- 商品网格 -->
                <div class="goods-grid" v-if="goodsList.length > 0">
                    <div class="goods-card" v-for="goods in goodsList" :key="goods.goods_id"
                        @click="goDetail(goods.goods_id)">
                        <a-image v-if="goods.goods_thumb" :src="goods.goods_thumb" class="goods-thumb" />
                        <div class="goods-info">
                            <h3 class="goods-name">{{ goods.goods_name }}</h3>
                            <div class="goods-meta">
                                <span class="goods-price">￥{{ goods.goods_price }}</span>
                                <span class="goods-stock" v-if="goods.goods_stock === -1">充足</span>
                                <span class="goods-stock" v-else>剩{{ goods.goods_stock }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <a-empty v-else-if="!goodsLoading" description="暂无商品" />

                <div class="pagination-wrapper" v-if="goodsTotal > goodsPageSize">
                    <a-pagination :current="goodsPage" :page-size="goodsPageSize" :total="goodsTotal"
                        :show-total="(total) => `共 ${total} 件商品`" @change="handleGoodsPageChange" />
                </div>
            </div>
        </main>

        <IndexFooter />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { message } from 'ant-design-vue';
import { TicketsOutlined } from '@ant-design/icons-vue';
import IndexHeader from '@/components/IndexHeader.vue'
import IndexFooter from '@/components/IndexFooter.vue'

const baseURL = process.env.VUE_APP_API_BASE_URL;

export default defineComponent({
    name: 'ShopHome',
    components: { IndexHeader, IndexFooter, TicketsOutlined },
    data() {
        return {
            tenantKey: '',
            // 小店信息
            shopInfo: {},
            // 分类
            categories: [],
            activeCategory: null,
            // 商品列表
            goodsList: [],
            goodsLoading: false,
            goodsPage: 1,
            goodsPageSize: 20,
            goodsTotal: 0,
        }
    },
    mounted() {
        this.tenantKey = this.$route.query.tenant_key || '';
        if (this.tenantKey) {
            this.getShopInfo();
            this.getCategories();
            this.getGoodsList();
        }
    },
    methods: {
        // 获取小店信息
        async getShopInfo() {
            try {
                const res = await axios.post(`${baseURL}/api/shop/info`, { tenant_key: this.tenantKey });
                if (res.data.code === 0 || res.data.code === 200) {
                    this.shopInfo = res.data.data || {};
                }
            } catch (e) {
                console.error('获取小店信息失败:', e);
            }
        },
        // 订单查询跳转
        goOrders() {
            this.$router.push({ path: '/shopOrders', query: { tenant_key: this.tenantKey } });
        },
        // 商品详情跳转
        goDetail(goodsId) {
            this.$router.push({ path: '/shopDetail', query: { tenant_key: this.tenantKey, goods_id: goodsId } });
        },
        // 分类
        async getCategories() {
            try {
                const res = await axios.post(`${baseURL}/api/shop/categories`, { tenant_key: this.tenantKey });
                if (res.data.code === 0 || res.data.code === 200) {
                    const data = res.data.data;
                    this.categories = Array.isArray(data) ? data : (data.list || []);
                } else {
                    message.error(res.data.msg || '获取分类失败');
                }
            } catch (e) {
                console.error('获取分类失败:', e);
                message.error('获取分类失败');
            }
        },
        selectCategory(catId) {
            this.activeCategory = catId;
            this.goodsPage = 1;
            this.getGoodsList();
        },
        // 商品列表
        async getGoodsList() {
            this.goodsLoading = true;
            try {
                const params = {
                    tenant_key: this.tenantKey,
                    page: this.goodsPage,
                    pageSize: this.goodsPageSize,
                };
                if (this.activeCategory !== null) {
                    params.category_id = this.activeCategory;
                }
                const res = await axios.post(`${baseURL}/api/shop/products`, params);
                if (res.data.code === 0 || res.data.code === 200) {
                    this.goodsList = res.data.data.list || [];
                    this.goodsTotal = res.data.data.total || 0;
                } else {
                    message.error(res.data.msg || '获取商品列表失败');
                }
            } catch (e) {
                console.error('获取商品列表失败:', e);
                message.error('获取商品列表失败');
            } finally {
                this.goodsLoading = false;
            }
        },
        handleGoodsPageChange(page) {
            this.goodsPage = page;
            this.getGoodsList();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
    }
})
</script>

<style scoped lang="scss">
.page {
    min-height: 100vh;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f8fafc 100%);
    display: flex;
    flex-direction: column;
}

.page-main {
    max-width: 1100px;
    margin: 0 auto;
    padding: 12vh 1.5rem 4rem;
    display: flex;
    flex-direction: column;
    height: 90vh;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.page-main::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
}

/* 错误提示 */
.error-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 2rem;
    background: #fff;
    border-radius: 1.4rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 22px 45px -30px rgba(239, 68, 68, 0.25);

    .error-icon {
        width: 64px;
        height: 64px;
        color: #ef4444;
        margin-bottom: 1rem;

        svg {
            width: 100%;
            height: 100%;
        }
    }

    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 0.5rem;
    }

    p {
        font-size: 0.95rem;
        color: #64748b;
        margin: 0.2rem 0;
    }

    .error-hint {
        color: #94a3b8;
        font-size: 0.85rem;
        margin-top: 0.5rem;
    }
}

/* 小店头部 */
.shop-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
}

.shop-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.shop-subtitle {
    font-size: 0.95rem;
    color: #64748b;
    margin: 0.2rem 0 0;
}

.shop-contact {
    font-size: 0.82rem;
    color: #94a3b8;
    margin: 0.3rem 0 0;
}

.orders-btn {
    border: 1px solid rgba(37, 99, 235, 0.3);
    color: #2563eb;
    background: rgba(37, 99, 235, 0.05);

    &:hover {
        background: rgba(37, 99, 235, 0.12);
    }
}

/* 分类标签 */
.category-tabs {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
}

.category-tab {
    padding: 0.4rem 1.1rem;
    border-radius: 9999px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #64748b;
    background: #fff;
    border: 1px solid rgba(226, 232, 240, 0.9);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: rgba(37, 99, 235, 0.3);
        color: #2563eb;
    }

    &.active {
        background: linear-gradient(135deg, #2563eb, #3b82f6);
        color: #fff;
        border-color: transparent;
    }
}

/* 商品网格 */
.goods-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1.2rem;
}

.goods-card {
    background: #fff;
    border-radius: 1rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 8px 16px -12px rgba(15, 23, 42, 0.15);
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 14px 24px -16px rgba(37, 99, 235, 0.3);
        border-color: rgba(37, 99, 235, 0.3);
    }
}

.goods-thumb {
    width: 100%;
    height: 160px;

    :deep(img) {
        width: 100%;
        height: 100%;
    }
}

.goods-info {
    padding: 0.9rem 1rem;
}

.goods-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 0.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.goods-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.goods-price {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2563eb;
}

.goods-stock {
    font-size: 0.75rem;
    color: #94a3b8;
}

/* 分页 */
.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin: 2rem 0 1.5rem;
}

/* 移动端响应式 */
@media (max-width: 768px) {
    .page-main {
        padding: 10vh 1rem 3rem;
    }

    .shop-title {
        font-size: 1.5rem;
    }

    .goods-grid {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 0.8rem;
    }

    .goods-thumb {
        height: 130px;
    }

    .goods-info {
        padding: 0.7rem 0.8rem;
    }

    .goods-name {
        font-size: 0.85rem;
    }

    .goods-price {
        font-size: 1rem;
    }
}
</style>
